@if (customReport$ | async; as customReport) {
  <pt-feature-info />
  <pt-breadcrumbs
    class="border-b border-solid border-slate-300 bg-white"
    [breadcrumbs]="breadcrumbs()"
    [current]="currentBreadcrumb()"
  />

  <div class="min-h-[4px]">
    @if (loader$ | async; as loader) {
      <mat-progress-bar [mode]="loader.mode" [value]="loader.progress" />
    }
  </div>

  <ng-container *ngrxLet="hasEditPermissions$ as hasEditPermissions">
    @if (hasEditPermissions) {
      <div *ptFeatureEnabled="marketingFeature">
        @if (draftMarketingCampaigns().length > 0) {
          <div class="flex flex-col gap-2 p-4">
            @for (
              campaign of draftMarketingCampaigns();
              track campaign.ref.path
            ) {
              <div
                class="bg-primary-50 flex flex-row items-center gap-4 rounded-md px-4 py-2"
              >
                <mat-icon class="text-primary-500">campaign</mat-icon>
                <div class="gap flex grow flex-col">
                  <a class="text-md font-bold">
                    {{ campaign.name }}
                  </a>
                  <div class="text-primary-200 text-sm">
                    @if (isInDraft()) {
                      You're building a report for your new marketing campaign.
                      Once it's ready, publish it to continue.
                    } @else {
                      Your report has been published successfully.
                    }
                  </div>
                </div>
                <button
                  mat-flat-button
                  color="primary"
                  (click)="publishOpenCampaign(campaign)"
                >
                  @if (isInDraft()) {
                    Publish Report &amp; Continue
                  } @else {
                    Continue Editing Campaign
                  }
                  <mat-icon iconPositionEnd>arrow_forward</mat-icon>
                </button>
              </div>
            }
          </div>
        }
      </div>
    }

    <div class="flex flex-col gap-4 p-4">
      @if (datasource()?.isRetired) {
        <pr-information-box class="col-span-full">
          This data source has been retired. We advise against using this report
          as the data source is no longer maintained. Please contact us to find
          alternate ways of reporting on this data.

          <button mat-stroked-button class="shrink-0" (click)="intercom.open()">
            Contact Us
          </button>
        </pr-information-box>
      }

      <div class="flex flex-row items-end gap-4">
        <div class="flex min-h-[44px] grow flex-wrap items-center gap-4">
          @if (hasEditPermissions && !isViewingHistoricalRun()) {
            <div class="flex grow">
              <a
                class="flex flex-row items-center"
                [routerLink]="[]"
                (click)="editCustomReport(customReport)"
              >
                <h1 class="!m-0 !font-bold">
                  {{ customReport.name }}
                </h1>
                <mat-icon>arrow_drop_down</mat-icon>
              </a>
            </div>
          } @else {
            <h1 class="!m-0 flex grow !font-bold">
              {{ customReport.name }}
            </h1>
          }
          <pr-run-custom-report-form
            [customReport]="customReport"
            [historicalRun]="activeHistoricalRun()"
            (runReport)="runReport($event)"
            (dateRangeChange)="dateRange.set($event)"
          />
        </div>
        <button
          mat-icon-button
          matTooltip="{{ sidenav.opened ? 'Close' : 'Open' }} sidebar"
          (click)="sidenav.toggle()"
        >
          <mat-icon fontSet="material-symbols">
            {{ sidenav.opened ? 'right_panel_close' : 'right_panel_open' }}
          </mat-icon>
        </button>
      </div>
    </div>

    <mat-sidenav-container autosize class="flex grow" [hasBackdrop]="false">
      <mat-sidenav
        #sidenav
        class="!w-[360px] border-l border-solid border-slate-100 !bg-[#fafafa] px-4"
        position="end"
        opened
        [mode]="breakpoint.isActive() ? 'over' : 'side'"
      >
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-4 rounded-md bg-white p-4 shadow-md">
            <div class="flex flex-col gap-2">
              <div class="flex flex-row items-center gap-1">
                <h3 class="!m-0 flex grow !font-bold">Report Details</h3>
                @if (isInDraft()) {
                  <pr-icon-chip icon="pending">Draft</pr-icon-chip>
                }
              </div>
              @if (customReport.description) {
                <div>{{ customReport.description }}</div>
              } @else {
                <div class="text-xs opacity-50">No Description</div>
              }
              <pr-data-source-display [dataSource]="datasource()" />
            </div>
            <div class="flex flex-col gap-1">
              <div>Restricted Access</div>
              @for (
                accessEntity of customReport.restrictAccessTo;
                track accessEntity.ref.path
              ) {
                <pr-query-filter-display
                  [label]="accessEntity.type | titlecase"
                  [values]="accessEntity.name"
                  [icon]="accessEntity | map: getAccessIcon"
                />
              } @empty {
                <div class="text-xs opacity-50">No Access Restrictions</div>
              }
            </div>

            @if (hasEditPermissions && !isViewingHistoricalRun()) {
              <div class="flex flex-row flex-wrap items-center gap-2">
                @if (isInDraft()) {
                  <button
                    mat-stroked-button
                    color="primary"
                    matTooltip="Publish this report for general access"
                    (click)="publishReport(customReport)"
                  >
                    <mat-icon>check_circle</mat-icon>
                    Publish
                  </button>
                } @else {
                  <button
                    mat-stroked-button
                    color="warn"
                    matTooltip="Remove report from general access"
                    (click)="updateReport({ status: reportStatus.Draft })"
                  >
                    <mat-icon>unpublished</mat-icon>
                    Revert to Draft
                  </button>
                }
                <button
                  mat-stroked-button
                  matTooltip="Change report details"
                  (click)="editCustomReport(customReport)"
                >
                  <mat-icon>edit</mat-icon>
                  Edit
                </button>
              </div>
            }
          </div>

          @if (hasEditPermissions) {
            <div
              *ptFeatureEnabled="marketingFeature"
              class="flex flex-col gap-4 rounded-md bg-white p-4 shadow-md"
            >
              <div class="flex flex-col gap-2">
                <h3 class="!m-0 !font-bold">Marketing Campaigns</h3>
                <div class="flex flex-col gap-1">
                  <div class="text-xs opacity-50">
                    This report can identify entries for the following scopes:
                  </div>
                  <pr-custom-report-scopes [scopes]="templateScopes()" />
                </div>
              </div>
              @for (campaign of marketingCampaigns(); track campaign.ref.path) {
                <div class="flex flex-row items-center">
                  <div class="flex grow">
                    <a
                      [prBrandRouterLink]="
                        (campaign | map: brandLinks.marketingCampaign).segments
                      "
                    >
                      {{ campaign.name }}
                    </a>
                  </div>
                  <pr-campaign-status [status]="campaign.status" />
                </div>
              } @empty {
                <div
                  class="flex flex-col gap-4"
                  ptIntercomTarget="custom-report.marketing-campaigns.button"
                >
                  <div class="text-xs opacity-50">
                    This report is not used in any Marketing Campaigns
                  </div>
                  <button
                    mat-stroked-button
                    color="primary"
                    (click)="createCampaign()"
                  >
                    <mat-icon>add</mat-icon>
                    Create Campaign From Report
                  </button>
                </div>
              }
            </div>
          }

          <div class="rounded-md bg-white p-4 shadow-md">
            <pr-report-run-history
              [customReport]="customReport"
            />
          </div>
        </div>
      </mat-sidenav>
      <mat-sidenav-content>
        <div class="flex flex-col gap-4 px-4 pb-4">
           @if (activeHistoricalRun(); as historicalRun) {
            <pr-historical-run-banner
              [run]="historicalRun"
              (backToCurrentReport)="backToCurrentReport()"
              (togglePin)="toggleHistoricalRunPin()"
              (archive)="archiveHistoricalRun()"
            />
          }
          @if (store.isError$ | async) {
            <pr-information-box theme="warn">
              Oops! There was a problem fetching your data. Please contact
              support if this issue persists.
            </pr-information-box>
          } @else if (store.hasNoData$ | async) {
            <pr-information-box>
              No data matched your filters. Try adjusting your date range or
              filters and run the report again.
            </pr-information-box>
          }
          <div class="flex flex-col gap-8">
            <pr-custom-report-filters
              [customReport]="effectiveCustomReport() ?? customReport"
              [readonly]="isViewingHistoricalRun()"
            />
            <pr-custom-report-charts
              [customReport]="effectiveCustomReport() ?? customReport"
              [readonly]="isViewingHistoricalRun()"
            />
            <pr-custom-report-table
              [customReport]="effectiveCustomReport() ?? customReport"
              [readonly]="isViewingHistoricalRun()"
            />
          </div>
        </div>
      </mat-sidenav-content>
    </mat-sidenav-container>
  </ng-container>
}
